import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { LogoImage, Template } from '../../types/types';

// Query response types
export interface GetTemplatesResponse { getTemplatesFromUserName: Template[] }
export interface GetTemplatesByOrganizationResponse { getTemplatesByOrganization: Template[] }
export interface GetLogoImageResponse { getLogoImage: LogoImage }
export interface GetLogoImagesResponse { getLogoImages: LogoImage[] }
export interface GetLogoImagesForUserResponse { getLogoImagesForUser: LogoImage[] }

const createTemplateMutation = gql`
    mutation createTemplate(
        $TemplateId: String!,
        $OrganizationId: String!,
        $TemplateName: String!,
        $NFTLocation: String!,
        $Height: Int!,
        $Width: Int!,
        $AwardDescription: TextFieldInput!,
        $RecipientName: TextFieldInput!,
        $GrantDate: TextFieldInput!,
        $AwardName: TextFieldInput!,
        $NFTSerialNumber: TextFieldInput!,
        $OrganizationLogo: ImageFieldInput!,
        $LaudLogo: ImageFieldInput!) {
        createTemplate(
            TemplateId: $TemplateId,
            OrganizationId: $OrganizationId,
            TemplateName: $TemplateName,
            NFTLocation: $NFTLocation,
            Height: $Height,
            Width: $Width,
            AwardDescription: $AwardDescription,
            RecipientName: $RecipientName,
            GrantDate: $GrantDate,
            AwardName: $AwardName,
            NFTSerialNumber: $NFTSerialNumber,
            OrganizationLogo: $OrganizationLogo,
            LaudLogo: $LaudLogo,
        ) {
            TemplateId
            OrganizationId
            TemplateName
            NFTLocation
            Height
            Width
            AwardDescription {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            RecipientName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            GrantDate {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            AwardName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            NFTSerialNumber {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            OrganizationLogo {
                left
                top
                height
                width
            }
            LaudLogo {
                left
                top
                height
                width
            }
        }
    }
`;

const deleteTemplateMutation = gql`
    mutation deleteTemplate(
        $TemplateName: String!,
        $OrganizationId: String!) {
        deleteTemplate(
            TemplateName: $TemplateName,
            OrganizationId: $OrganizationId) {
            TemplateName
        }
    }
`;

const addLogoImageMutation = gql`
    mutation addLogoImage(
        $OrganizationId: String!,
        $LogoImageId: String!,
        $LogoImageName: String!,
        $LogoImageLocation: String!) {
        addLogoImage(
            OrganizationId: $OrganizationId,
            LogoImageId: $LogoImageId,
            LogoImageName: $LogoImageName,
            LogoImageLocation: $LogoImageLocation,
        ) {
            OrganizationId
            LogoImageId
            LogoImageName
            LogoImageLocation
        }
    }
`;

const deleteLogoImageMutation = gql`
    mutation deleteLogoImage(
        $OrganizationId: String!,
        $LogoImageId: String!) {
        deleteLogoImage(
            OrganizationId: $OrganizationId,
            LogoImageId: $LogoImageId,
        ) {
            OrganizationId
            LogoImageId
        }
    }
`;

const templateQuery = gql`
    query getTemplates(
        $UserName: String!) {
            getTemplatesFromUserName(UserName: $UserName) {
            TemplateName
            OrganizationId
            NFTLocation
            RecipientName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            AwardDescription {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            GrantDate {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            TemplateId
            Width
            Height
            AwardName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            NFTSerialNumber {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            OrganizationLogo {
                left
                top
                height
                width
            }
            LaudLogo {
                left
                top
                height
                width
            }
        }
    }
`;

function useTemplateQuery(userName: string) {
    return useQuery<GetTemplatesResponse>(
        templateQuery,
        {
            variables: {
                UserName: userName,
            },
            fetchPolicy: 'cache-and-network',
        },
    );
}

const templateByOrganizationQuery = gql`
    query getTemplatesByOrganization(
        $OrganizationId: String!) {
            getTemplatesByOrganization(OrganizationId: $OrganizationId) {
            TemplateName
            OrganizationId
            NFTLocation
            RecipientName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            AwardDescription {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            GrantDate {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            TemplateId
            Width
            Height
            AwardName {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            NFTSerialNumber {
                left
                top
                height
                width
                fontFamily
                fontSize
                stroke
                angle
            }
            OrganizationLogo {
                left
                top
                height
                width
            }
            LaudLogo {
                left
                top
                height
                width
            }
        }
    }
`;

function useTemplatesByOrganizationQuery(organizationId: string) {
    return useQuery<GetTemplatesByOrganizationResponse>(
        templateByOrganizationQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const logoImageQuery = gql`
    query getLogoImage(
        $OrganizationId: String!,
        $LogoImageId: String!) {
        getLogoImage(
            OrganizationId: $OrganizationId,
            LogoImageId: $LogoImageId) {
            OrganizationId
            LogoImageId
            LogoImageLocation
            LogoImageName
        }
    }
`;

function useLogoImageQuery(organizationId: string, logoImageId: string) {
    return useQuery<GetLogoImageResponse>(
        logoImageQuery,
        {
            variables: {
                OrganizationId: organizationId,
                LogoImageId: logoImageId,
            },
        },
    );
}

const logoImagesQuery = gql`
    query getLogoImages(
        $OrganizationId: String!) {
        getLogoImages(OrganizationId: $OrganizationId) {
            OrganizationId
            LogoImageId
            LogoImageLocation
            LogoImageName
        }
    }
`;

function useLogoImagesQuery(organizationId: string) {
    return useQuery<GetLogoImagesResponse>(
        logoImagesQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const logoImagesForUserQuery = gql`
    query getLogoImagesForUser(
        $UserName: String!) {
        getLogoImagesForUser(UserName: $UserName) {
            OrganizationId
            LogoImageId
            LogoImageLocation
            LogoImageName
        }
    }
`;

function useLogoImagesForUserQuery(userName: string) {
    return useQuery<GetLogoImagesForUserResponse>(
        logoImagesForUserQuery,
        {
            variables: {
                UserName: userName,
            },
            fetchPolicy: 'cache-and-network',
        },
    );
}

export {
    templateQuery,
    templateByOrganizationQuery,
    logoImageQuery,
    logoImagesQuery,
    logoImagesForUserQuery,
    createTemplateMutation,
    deleteTemplateMutation,
    addLogoImageMutation,
    deleteLogoImageMutation,
    useTemplateQuery,
    useTemplatesByOrganizationQuery,
    useLogoImageQuery,
    useLogoImagesQuery,
    useLogoImagesForUserQuery,
};
